import {
  Button,
  ButtonGroup,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  Row,
  Table,
  useDefaultSelecting,
} from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useConfirm } from '../../../hooks/useConfirm';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useTimetableVersionDataQuery } from '../../../types/planung-graphql-client-defs';
import { useTimetableStore } from '../../TimetableGrid/TimetableProvider';
import { TimetableVersionTableType } from '../graphql/types';
import { createTimetableVersionTableColumns } from '../utils/timetableVersionTableUtils';

type TimetableVersionRoomsTableProps = {
  versionUuid: string;
  onRowSelection: (rowData: TimetableVersionTableType, index: number) => void;
  onShowLessonClick: (uuid: string) => void;
  versionRoomUuids: string[];
  search: string;
  onRemove: (uuids: string[]) => void;
};

export const TimetableVersionRoomsTableTable = observer(
  ({
    onRowSelection,
    onShowLessonClick,
    versionRoomUuids,
    versionUuid,
    search,
    onRemove,
  }: TimetableVersionRoomsTableProps) => {
    const { t } = useTranslation();
    const isMobile = useIsMobile();

    const { sorting, saveSorting } = useColumnsSort('timetableVersionRoomsTable');
    const { rowSelection, onRowSelectionChange } = useDefaultSelecting({ 0: true });

    const [{ data: versionData }] = useTimetableVersionDataQuery({
      variables: { versionUuid },
      pause: !versionUuid,
    });
    const roomsView = versionData?.timetableVersionData.view.rooms;
    const editorLessons = versionData?.timetableVersionData.editor?.lessons;

    const rooms: TimetableVersionTableType[] = useMemo(() => {
      return (
        roomsView
          ?.filter((r) => versionRoomUuids.includes(r.uuid))
          .map((r) => {
            return {
              color: {},
              count: editorLessons?.filter((l) => l.roomSupplyUuids.includes(r.uuid)).length ?? 0,
              name: r.name,
              shortName: r.roomNumber ?? '',
              uuid: r.uuid,
            };
          }) ?? []
      );
    }, [roomsView, versionRoomUuids, editorLessons]);

    const columns = useMemo(() => createTimetableVersionTableColumns(t('common.name') as string), [t]);

    const store = useTimetableStore();

    const canBeRemoved = (uuids: string[]) => {
      return uuids.every((uuid) => {
        let roomUsed = false;
        store.cardsBySlotAndResource.forEach((r, key) => {
          if (key.includes(uuid)) {
            roomUsed = true;
          }
        });
        return !roomUsed;
      });
    };

    const { confirm, ConfirmationDialog } = useConfirm();

    const handleRemove = async (uuids: string[]) => {
      const res = await confirm({
        title: t('timetableVersion.removeRooms', { count: uuids.length }),
        message: t('timetableVersion.removeRoomsDescription', { count: uuids.length }),
      });
      if (res) {
        onRemove(uuids);
      }
    };
    const readOnly = store.readonly;
    return (
      <>
        <Table<TimetableVersionTableType>
          isDesktop={!isMobile}
          showBorderRadius
          showShadow
          canScroll
          minHeight={900}
          sorting={sorting}
          onSortingChange={saveSorting}
          columns={columns}
          data={rooms}
          globalFilter={search}
          lastColWidth='140px'
          lastCol={(row: Row<TimetableVersionTableType>) => {
            return (
              <ButtonGroup>
                <Button hierarchy='secondary' onClick={() => onShowLessonClick(row.original.uuid)}>
                  {t('timetableVersion.showLessons')}
                </Button>
                <Dropdown
                  noPadding
                  trigger={<Button hierarchy='secondary' icon={<DotsHorizontalIcon className='small' />} />}
                >
                  <DropdownMenu
                    data={[
                      {
                        label: t('timetableVersion.editData'),
                        disabled: true,
                        onClick: () => {
                          console.log('edit clicked');
                        },
                      },
                      {
                        label: t('timetableVersion.test'),
                      },
                      {
                        label: t('timetableVersion.remove'),
                        type: 'default',
                        color: 'error',
                        disabled: !canBeRemoved([row.original.uuid]) || readOnly,
                        onClick: async () => {
                          await handleRemove([row.original.uuid]);
                        },
                      },
                    ]}
                  />
                </Dropdown>
              </ButtonGroup>
            );
          }}
          showRowHover
          onRowClick={(event, row) => {
            if (onRowSelectionChange) onRowSelectionChange({ [row.id]: true });
            onRowSelection(row.original, row.index);
            document.querySelector('#content')?.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          onRowSelectionChange={onRowSelectionChange}
          rowSelection={rowSelection}
          showSelected
        />
        <ConfirmationDialog />
      </>
    );
  },
);
